import type React from "react";
import { useEffect, useState } from "react";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { faUsers } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse, IconButton, Stack } from "@mui/material";
import {
  type GridCsvExportOptions,
  type GridCsvGetRowsToExportParams,
  gridExpandedSortedRowIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";

import { useGetProfessionals } from "api/services/admin/hooks/useGetProfessionals";
import { BaseButton } from "shared/atoms/BaseButton";
import { ButtonsGroup } from "shared/atoms/ButtonsGroup";
import { MUISelect } from "shared/atoms/MUI";
import { WidgetBox } from "shared/containers/WidgetBox";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay/EmptyResultOverlay";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import { getStartOfMonth } from "utils/date";

import { useMonthOptions } from "../utils";

import { useTeamMembersColumns } from "./useTeamMembersColumns";

type TabView = "performance" | "details";

const getFilteredRows = ({ apiRef }: GridCsvGetRowsToExportParams) => gridExpandedSortedRowIdsSelector(apiRef);

interface Props {
  hcoID: number;
  hcpID?: number;
}

export const TeamMembers: React.FC<Props> = ({ hcoID, hcpID }) => {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 });
  const [expanded, setExpanded] = useState(true);
  const [tab, setTab] = useState<TabView>("performance");
  const [selectedMonth, setSelectedMonth] = useState(getStartOfMonth());
  const { monthOptions } = useMonthOptions();

  const { data: professionalsData, isLoading } = useGetProfessionals({
    health_care_organization_id: hcoID,
    params: { hcp_id: hcpID, month: selectedMonth },
  });
  const professionals = professionalsData?.professionals || [];

  useEffect(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [hcpID]);

  const isCurrentMonth = selectedMonth === monthOptions[0].value;
  const { detailsColumns, performanceColumns } = useTeamMembersColumns({ isCurrentMonth });

  const handleExport = (options: GridCsvExportOptions) => apiRef.current.exportDataAsCsv(options);
  const csvFilename = `${t("admin.team_members.title").replaceAll(/\s+/g, "_")}_${tab}_${new Date().toISOString()}`;

  const columns = tab === "performance" ? performanceColumns : detailsColumns;

  return (
    <WidgetBox
      title={t("admin.team_members.title")}
      description={t("admin.team_members.description", { count: professionals.length })}
      icon={<FontAwesomeIcon icon={faUsers} />}
      color="teal"
      toolbar={
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          {tab === "performance" && (
            <MUISelect
              size="small"
              options={monthOptions}
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value as string)}
              sx={{ minWidth: 160 }}
            />
          )}
          <ButtonsGroup
            group={[
              { label: t("admin.team_members.tab_performance"), value: "performance" },
              { label: t("admin.team_members.tab_details"), value: "details" },
            ]}
            active={tab}
            setActive={value => setTab(value as TabView)}
          />
          <BaseButton
            variant="action"
            onClick={() => handleExport({ getRowsToExport: getFilteredRows, fileName: csvFilename, allColumns: true })}
            icon="arrow-down-from-bracket"
            text={t("buttons.export_csv")}
          />
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} style={{ fontSize: 14 }} />
          </IconButton>
        </Stack>
      }
      skipPadding
    >
      <Collapse in={expanded}>
        <MUIDataGrid
          apiRef={apiRef}
          columns={columns}
          rows={professionals}
          getRowId={row => row.id}
          loading={isLoading}
          paginationModel={paginationModel}
          onPaginationModelChange={model => setPaginationModel(model)}
          rowCount={professionals.length}
          rowSelection={false}
          slots={{
            noRowsOverlay: () => <EmptyResultOverlay />,
            noResultsOverlay: () => <EmptyResultOverlay />,
          }}
          localeText={{
            noRowsLabel: "",
            noResultsOverlayLabel: "",
          }}
          columnVisibilityModel={tab === "performance" ? { email: false } : {}}
          disableColumnMenu
          hideHeader
          transparentBackground
          density="comfortable"
          sx={{
            "& .MuiDataGrid-row": {
              cursor: "default",
            },
          }}
        />
      </Collapse>
    </WidgetBox>
  );
};
